const frames = new Map();
const pending = [];
let active = 0;

function pump() {
  while (active < 2 && pending.length) {
    const { url, resolve } = pending.shift();
    active++;
    capture(url).then(resolve, () => resolve(null)).finally(() => { active--; pump(); });
  }
}

export function frameFromVideo(url) {
  if (!url) return Promise.resolve(null);
  if (!frames.has(url)) frames.set(url, new Promise(resolve => { pending.push({ url, resolve }); pump(); }));
  return frames.get(url);
}

function capture(url) {
  return new Promise(resolve => {
    const video = document.createElement('video');
    let done = false;
    let drawing = false;
    let timer;
    let best = null;
    let bestScore = -1;
    let index = 0;
    const finish = result => {
      if (done) return;
      done = true;
      clearTimeout(timer);
      video.onloadedmetadata = video.onseeked = video.onerror = null;
      video.pause();
      video.removeAttribute('src');
      video.load();
      resolve(result);
    };
    const seek = () => {
      const duration = Number.isFinite(video.duration) ? video.duration : 2;
      const points = [0.15, 0.55, 1.1];
      const target = Math.max(0, Math.min(points[index], duration * 0.8));
      try { video.currentTime = target; } catch { finish(best); }
    };
    video.onseeked = () => {
      if (done || drawing || video.readyState < 2 || !video.videoWidth) return;
      drawing = true;
      try {
        const canvas = document.createElement('canvas');
        const scale = Math.min(1, 1600 / Math.max(video.videoWidth, video.videoHeight));
        canvas.width = Math.round(video.videoWidth * scale);
        canvas.height = Math.round(video.videoHeight * scale);
        canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
        const small = document.createElement('canvas');
        small.width = 128; small.height = 128;
        const context = small.getContext('2d', { willReadFrequently: true });
        context.drawImage(video, 0, 0, 128, 128);
        const data = context.getImageData(0, 0, 128, 128).data;
        let score = 0;
        for (let y = 1; y < 127; y++) for (let x = 1; x < 127; x++) {
          const i = (y * 128 + x) * 4;
          const edge = data[i] * 4 - data[i - 4] - data[i + 4] - data[i - 512] - data[i + 512];
          score += edge * edge;
        }
        canvas.toBlob(blob => {
          drawing = false;
          if (done) return;
          if (blob && score > bestScore) {
            if (best) URL.revokeObjectURL(best);
            best = URL.createObjectURL(blob); bestScore = score;
          }
          index++;
          if (index < 3) seek(); else finish(best);
        }, 'image/jpeg', 0.94);
      } catch { finish(best); }
    };
    video.crossOrigin = 'anonymous';
    video.muted = true;
    video.playsInline = true;
    video.preload = 'metadata';
    video.onloadedmetadata = seek;
    video.onerror = () => finish(best);
    timer = setTimeout(() => finish(best), 20000);
    video.src = url;
    video.load();
  });
}

const observed = new WeakSet();
const apply = video => {
  if (!video.isConnected) return;
  const src = video.dataset.metVideoSrc || video.currentSrc || video.getAttribute('src') || video.querySelector('source')?.src;
  if (!src || video.closest('met-cullen-preview')) return;
  const old = video.getAttribute('poster');
  if (old && !old.startsWith('blob:') && !/\/videos\//.test(old)) video.removeAttribute('poster');
  frameFromVideo(src).then(poster => {
    if (!poster || !video.isConnected) return;
    const now = video.dataset.metVideoSrc || video.currentSrc || video.getAttribute('src') || video.querySelector('source')?.src;
    if (now !== src) return;
    video.poster = poster;
    video.dataset.metPosterFromVideo = src;
    const gallery = video.closest('met-cdn-gallery');
    if (gallery) {
      const index = [...gallery.querySelectorAll('[data-met-slide]')].indexOf(video.closest('[data-met-slide]'));
      const thumb = gallery.querySelectorAll('[data-met-thumb]')[index];
      if (thumb) {
        let image = thumb.querySelector('img');
        if (!image) { image = document.createElement('img'); image.alt = ''; image.width = 100; image.height = 100; thumb.prepend(image); }
        image.src = poster;
      }
    }
  });
};
const observer = new IntersectionObserver(entries => {
  for (const entry of entries) if (entry.isIntersecting) { observer.unobserve(entry.target); apply(entry.target._metFrameVideo || entry.target); }
}, { rootMargin: '120px' });
const scan = root => {
  const galleries = root.matches?.('met-cdn-gallery') ? [root] : [...(root.querySelectorAll?.('met-cdn-gallery') || [])];
  for (const gallery of galleries) {
    const slides = [...gallery.querySelectorAll('[data-met-slide]')];
    gallery.querySelectorAll('[data-met-thumb]').forEach((thumb, index) => {
      const video = slides[index]?.querySelector('video');
      if (!video || observed.has(thumb)) return;
      observed.add(thumb);
      thumb._metFrameVideo = video;
      observer.observe(thumb);
    });
  }
  const videos = root.matches?.('video') ? [root] : [...(root.querySelectorAll?.('video') || [])];
  for (const video of videos) {
    if (observed.has(video) || video.closest('met-cullen-preview')) continue;
    observed.add(video);
    const poster = video.getAttribute('poster');
    if (poster && !poster.startsWith('blob:') && !/\/videos\//.test(poster)) video.removeAttribute('poster');
    observer.observe(video);
  }
};
scan(document);
new MutationObserver(records => {
  for (const record of records) for (const node of record.addedNodes) if (node.nodeType === 1) scan(node);
}).observe(document.documentElement, { childList: true, subtree: true });
